import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@entities/auth';
import type { ReasonFactorValue } from '@shared/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger, useToast } from '@shared/ui';
import { swrKeys } from '@shared/api/swr/keys';
import { isApiError } from '@shared/api/errors/errors';
import { getApiErrorMessage } from '@shared/lib/get-api-error-message';
import { useApiServices } from '@shared/lib/use-api-services';
import type { ComplianceDecision } from '@shared/api/contracts/compliance';
import type { WithdrawalDecisionKind } from '@features/compliance-decision';
import { ComplianceCard } from '@widgets/compliance-card';
import { BlockedAddressesPanel, ComplianceQueue, PendingDecisionsPanel, ReviewersPanel } from '@widgets/compliance-queue';

const POLL_MS = 20000;

export function CompliancePage() {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const { user } = useAuth();
  const services = useApiServices();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.isAdmin === true || user?.role === 'ADMIN';
  const status = params.get('status') ?? '';
  const tab = params.get('tab') ?? 'queue';
  const withdrawalId = params.get('withdrawal');
  const userId = user?.id ? Number(user.id) : null;

  const queue = useSWR(
    isAdmin ? swrKeys.compliance.queue({ status, page, limit: 20 }) : null,
    () => services.compliance.listWithdrawals({ status: status || undefined, page, limit: 20 }),
    { refreshInterval: POLL_MS },
  );
  const card = useSWR(
    isAdmin && withdrawalId ? swrKeys.compliance.card(withdrawalId) : null,
    () => services.compliance.getWithdrawal(withdrawalId!),
    { refreshInterval: POLL_MS },
  );
  const decisions = useSWR(isAdmin ? swrKeys.compliance.decisions() : null, () => services.compliance.listPendingDecisions(), {
    refreshInterval: POLL_MS,
  });
  const addresses = useSWR(isAdmin ? swrKeys.compliance.addresses() : null, () => services.compliance.listBlockedAddresses(), {
    refreshInterval: POLL_MS,
  });
  const reviewers = useSWR(isAdmin ? swrKeys.compliance.reviewers() : null, () => services.compliance.listReviewers(), {
    refreshInterval: POLL_MS,
  });

  if (!isAdmin) return <Navigate to="/balance" replace />;

  const reviewer = reviewers.data?.data.some((item) => String(item.id) === user?.id && item.complianceReviewer) ?? false;

  const reload = () => {
    void queue.mutate();
    void card.mutate();
    void decisions.mutate();
    void addresses.mutate();
    void reviewers.mutate();
  };

  const run = async <T,>(action: () => Promise<T>, success: (result: T) => string): Promise<T | null> => {
    setBusy(true);
    try {
      const result = await action();
      toast(success(result), 'success');
      reload();
      return result;
    } catch (error: unknown) {
      toast(getApiErrorMessage(error, t, 'compliance.errors.generic'), 'error');
      if (isApiError(error) && (error.status === 403 || error.status === 409)) reload();
      return null;
    } finally {
      setBusy(false);
    }
  };

  const openWithdrawal = (id: number | string) => {
    const next = new URLSearchParams(params);
    next.set('withdrawal', String(id));
    setParams(next);
  };

  const setTab = (value: string) => {
    const next = new URLSearchParams(params);
    next.set('tab', value);
    setParams(next);
  };

  const setStatus = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set('status', value);
    else next.delete('status');
    setParams(next);
    setPage(1);
  };

  return (
    <div className="page-stack">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto w-full flex-wrap justify-start">
          <TabsTrigger value="queue">{t('compliance.tabs.queue')}</TabsTrigger>
          <TabsTrigger value="second">{t('compliance.tabs.second')}</TabsTrigger>
          <TabsTrigger value="addresses">{t('compliance.tabs.addresses')}</TabsTrigger>
          <TabsTrigger value="reviewers">{t('compliance.tabs.reviewers')}</TabsTrigger>
        </TabsList>
        <TabsContent value="queue">
          <ComplianceQueue
            items={queue.data?.data ?? []}
            page={queue.data?.pagination.page ?? page}
            limit={queue.data?.pagination.limit ?? 20}
            total={queue.data?.pagination.total ?? 0}
            status={status}
            onStatus={setStatus}
            onPage={setPage}
            onOpen={openWithdrawal}
          />
        </TabsContent>
        <TabsContent value="second">
          <PendingDecisionsPanel
            items={decisions.data?.data ?? []}
            userId={Number.isSafeInteger(userId) ? userId : null}
            loading={busy}
            onOpenWithdrawal={openWithdrawal}
            onResolve={async (id, action, value) => {
              const result = await run(
                () => {
                  if (action === 'confirm') return services.compliance.confirmDecision(id, value.secondFactor);
                  const payload = { reason: value.reason, secondFactor: value.secondFactor };
                  return action === 'reject'
                    ? services.compliance.rejectDecision(id, payload)
                    : services.compliance.cancelDecision(id, payload);
                },
                () => t(action === 'confirm' ? 'compliance.toast.applied' : 'compliance.toast.updated'),
              );
              return Boolean(result);
            }}
          />
        </TabsContent>
        <TabsContent value="addresses">
          <BlockedAddressesPanel
            items={addresses.data?.data ?? []}
            reviewer={reviewer}
            loading={busy}
            onUnblock={async (id, value) => {
              const result = await run(
                () => services.compliance.unblockAddress(id, { reason: value.reason, secondFactor: value.secondFactor }),
                (saved) =>
                  t(saved.state === 'PENDING_SECOND' ? 'compliance.toast.pendingSecond' : 'compliance.toast.applied'),
              );
              return Boolean(result);
            }}
          />
        </TabsContent>
        <TabsContent value="reviewers">
          <ReviewersPanel
            items={reviewers.data?.data}
            listLoading={reviewers.isLoading}
            listError={reviewers.error}
            listRetrying={reviewers.isValidating}
            onRetry={() => void reviewers.mutate()}
            userId={Number.isSafeInteger(userId) ? userId : null}
            loading={busy}
            onToggle={async (id, enabled, value) => {
              const result = await run(
                () =>
                  services.compliance.setReviewer(id, {
                    enabled,
                    reason: value.reason,
                    secondFactor: value.secondFactor,
                  }),
                () => t('compliance.toast.updated'),
              );
              return Boolean(result);
            }}
          />
        </TabsContent>
      </Tabs>
      <ComplianceCard
        open={Boolean(withdrawalId)}
        card={card.data ?? null}
        loading={Boolean(card.isLoading)}
        reviewer={reviewer}
        actionLoading={busy}
        onOpenChange={(open) => {
          if (open) return;
          const next = new URLSearchParams(params);
          next.delete('withdrawal');
          setParams(next);
        }}
        onDecide={async (decision: WithdrawalDecisionKind, value: ReasonFactorValue) => {
          if (!withdrawalId) return false;
          const result = await run(
            () =>
              services.compliance.decideWithdrawal(withdrawalId, {
                decision,
                reason: value.reason,
                ...(decision === 'REJECT' && value.lockOwner ? { lockOwner: true } : {}),
                secondFactor: value.secondFactor,
              }),
            (saved: ComplianceDecision) =>
              t(saved.state === 'PENDING_SECOND' ? 'compliance.toast.pendingSecond' : 'compliance.toast.applied'),
          );
          return Boolean(result);
        }}
        onReply={async (text) => {
          if (!withdrawalId) return;
          await services.compliance.postWithdrawalMessage(withdrawalId, text);
          toast(t('compliance.toast.message'), 'success');
          void card.mutate();
        }}
      />
    </div>
  );
}
